import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  convertWeight,
  fetchMaxesMap,
  loadStepFor,
  modelFromSource,
  type Exercise,
  type Locale,
  type ModelChangedRow,
  type ModelSourceDay,
  type ModelSourceRow,
  type ProgramModel,
  type RoundType,
  type UnitsPreference,
} from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { Dialog, DialogScroll } from "../primitives/Dialog";
import { TextField } from "../primitives/TextField";
import { writeError, type DurableWriteFn } from "../seams";
import type { UiTheme } from "../theme";

type Saved = { name: string; queued: boolean; toHistory: string[]; noLoad: string[]; changed: { row: string; why: string[] }[]; warmUps: number; model: Omit<ProgramModel, "id"> };
type Failure = "maxes" | "cap" | "save";

const NOOP = () => {};

/** What a model is made from: a program's days, or a logged session's one. */
export type ModelDialogSource = {
  days: readonly ModelSourceDay[];
  roundTypes: readonly RoundType[];
  sessionsPerWeek: number;
  /** Empty: a fallback that says the shape. */
  description: string;
  locale: Locale | null;
  /** A row's day in the summary. Absent: the row says its exercise only. */
  dayLabel?: (day: number) => string;
  /** Sets a logged session left out as warm-up. */
  warmUps?: number;
  /** Bodyweight exercises the catalog may not flag (workoutToSlotDrafts). */
  bodyweight?: readonly string[];
};

export type ModelSaveDialogProps = {
  theme: UiTheme;
  /** Read at save, so a source that cannot be read fails as the save does. */
  source: () => ModelDialogSource;
  defaultName: string;
  /** Which exercises are bodyweight. null while the host loads it: no save. */
  catalog: Exercise[] | null;
  /** The author's unit: the loads are read in it, the MAXes converted to it. */
  unit: UnitsPreference;
  apiFetch: ProgramApiFetch;
  durableWrite: DurableWriteFn;
  resolveName: (slug: string, snapshot?: string) => string;
  /** The saved model, to plan a program from it. Absent: no such action. */
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  onClose: () => void;
};

// #354: «Save as model». Asks a name, turns the source into a model
// (modelFromSource, against the MAXes of now) and says which rows lost their
// load progression, which have no load and which the model holds differently.
export function ModelSaveDialog({
  theme,
  source,
  defaultName,
  catalog,
  unit,
  apiFetch,
  durableWrite,
  resolveName,
  onPlanFromModel,
  onClose,
}: ModelSaveDialogProps) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<Failure | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy || !catalog) return;
    setBusy(true);
    setFailed(null);
    try {
      const src = source();
      // No MAXes, no save: rows would go to history by mistake.
      let maxesKg: Map<string, number>;
      try {
        maxesKg = await fetchMaxesMap(apiFetch);
      } catch (e) {
        console.warn("[library] save_model_maxes", e instanceof Error ? e.message : e);
        setFailed("maxes");
        return;
      }
      const maxes = new Map([...maxesKg].map(([slug, kg]) => [slug, convertWeight(kg, "kg", unit)]));
      // The fallback is written in the source's language, not the reader's.
      const written = i18n.getFixedT(src.locale ?? "en");
      const out = modelFromSource({
        name: trimmed,
        description:
          src.description.trim() ||
          written("library.modelDescFallback", {
            days: written("library.modelShapeDays", { count: src.days.length, defaultValue: "{{count}} days" }),
            rounds: written("editorV3.tetrad.roundsA11y", {
              count: src.roundTypes.length,
              defaultValue: "{{count}} rounds",
            }),
            defaultValue: "{{days}}, {{rounds}}.",
          }),
        locale: src.locale,
        roundTypes: src.roundTypes,
        sessionsPerWeek: src.sessionsPerWeek,
        days: src.days,
        maxes,
        bodyweight: new Set([...catalog.filter((e) => e.isBodyweight).map((e) => e.slug), ...(src.bodyweight ?? [])]),
        loadQuantum: loadStepFor(unit),
      });
      // Mobile may still queue it, if the connection drops after the read.
      const result = await durableWrite({
        method: "POST",
        path: "/api/templates/models?scope=mine",
        body: out.model,
      });
      const err = writeError(result);
      if (err) {
        console.warn("[library] save_model_failed", err.message);
        const code = (err as unknown as { body?: { error?: unknown } }).body?.error;
        setFailed(code === "model_cap" ? "cap" : "save");
        return;
      }
      const label = (r: ModelSourceRow) => {
        const exercise = resolveName(r.exerciseId, src.days[r.day]?.slots[r.slot]?.exerciseName);
        if (!src.dayLabel) return exercise;
        return t("library.modelRow", { exercise, day: src.dayLabel(r.day), defaultValue: "{{exercise}}, {{day}}" });
      };
      const changedRow = (r: ModelChangedRow) => ({
        row: label(r),
        why: [
          ...(r.byHand ? [t("library.modelChangedByHand", { defaultValue: "hand-set cells dropped" })] : []),
          ...(r.everyRound ? [t("library.modelChangedEveryRound", { defaultValue: "runs every round" })] : []),
        ],
      });
      setSaved({
        name: trimmed,
        queued: result.outcome === "queued",
        toHistory: out.toHistory.map(label),
        noLoad: out.noLoad.map(label),
        changed: out.changed.map(changedRow),
        warmUps: src.warmUps ?? 0,
        model: out.model,
      });
    } catch (e) {
      console.warn("[library] save_model_failed", e instanceof Error ? e.message : e);
      setFailed("save");
    } finally {
      setBusy(false);
    }
  };

  // A short title, one line of why, then hanging-indent rows: the bullet
  // sits in its own box so a wrapped line does not restart under it.
  const list = (title: string, why: string | null, rows: { row: string; why?: string[] }[]) =>
    rows.length === 0 ? null : (
      <View style={styles.group}>
        <Text style={styles.groupTitle}>{title}</Text>
        {why ? <Text style={styles.body}>{why}</Text> : null}
        {rows.map((r, i) => (
          <View key={`${i}-${r.row}`} style={styles.bulletRow}>
            <Text style={styles.body}>{"\u2022"}</Text>
            <View style={[styles.flex, styles.item]}>
              <Text style={styles.body}>{r.row}</Text>
              {(r.why ?? []).map((w) => (
                <Text key={w} style={styles.body}>
                  {w}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </View>
    );

  if (saved) {
    const kept = saved.toHistory.length === 0 && saved.noLoad.length === 0 && saved.changed.length === 0;
    return (
      <Dialog
        theme={theme}
        visible
        onRequestClose={onClose}
        title={t("library.modelSavedTitle", { defaultValue: "Model saved" })}
      >
        <DialogScroll contentContainerStyle={styles.scroll}>
          <Text style={styles.body} accessibilityLiveRegion="polite" aria-live="polite">
            {t("library.modelSavedBody", {
              name: saved.name,
              defaultValue: "\"{{name}}\" is in New program, under My models.",
            })}
            {saved.queued
              ? ` ${t("library.modelQueued", { defaultValue: "It saves when you are back online." })}`
              : ""}
          </Text>
          {kept ? (
            <Text style={styles.body}>
              {t("library.modelAllKept", { defaultValue: "Every row kept its progression." })}
            </Text>
          ) : null}
          {saved.warmUps > 0 ? (
            <Text style={styles.body}>
              {t("library.modelWarmUps", {
                count: saved.warmUps,
                defaultValue: "{{count}} warm-up sets left out.",
              })}
            </Text>
          ) : null}
          {list(
            t("library.modelToHistory", { defaultValue: "Load from history" }),
            t("library.modelToHistoryWhy", {
              defaultValue: "No MAX, or added weight on a bodyweight move. It won't climb.",
            }),
            saved.toHistory.map((row) => ({ row })),
          )}
          {list(
            t("library.modelNoLoad", { defaultValue: "No load" }),
            t("library.modelNoLoadWhy", { defaultValue: "The weight is picked in the new program." }),
            saved.noLoad.map((row) => ({ row })),
          )}
          {list(
            t("library.modelChanged", { defaultValue: "Changed in the model" }),
            null,
            saved.changed,
          )}
        </DialogScroll>
        {/* Source order reads the main action first; row-reverse keeps Done on the left. */}
        <View style={[styles.actions, styles.actionsReverse]}>
          {onPlanFromModel ? (
            <Button
              theme={theme}
              label={t("library.modelPlan", { defaultValue: "Plan a program" })}
              numberOfLines={2}
              style={styles.actionButton}
              onPress={() => {
                onClose();
                onPlanFromModel(saved.model);
              }}
            />
          ) : null}
          <Button
            theme={theme}
            variant={onPlanFromModel ? "secondary" : "primary"}
            label={t("library.modelDone", { defaultValue: "Done" })}
            onPress={onClose}
            style={styles.actionButton}
          />
        </View>
      </Dialog>
    );
  }

  const nameMissing = name.trim() === "";
  // Why Save is off, said in words beside it.
  const blocked = nameMissing
    ? t("library.modelNameEmpty", { defaultValue: "Give it a name to save." })
    : !catalog
      ? t("library.modelWaitCatalog", { defaultValue: "Loading the exercises…" })
      : null;
  const failure =
    failed === "maxes"
      ? t("library.modelMaxesError", { defaultValue: "Reading your MAXes needs a connection. Try again online." })
      : failed === "cap"
        ? t("library.modelCapError", {
            defaultValue: "You have 100 models, the most you can keep. Delete one in New program, My models.",
          })
        : failed === "save"
          ? t("library.modelSaveError", { defaultValue: "Could not save the model. Try again." })
          : null;

  return (
    <Dialog
      theme={theme}
      visible
      // Android back while the POST runs: stay.
      onRequestClose={busy ? NOOP : onClose}
      dismissOnBackdropPress={!busy}
      title={t("library.saveAsModel", { defaultValue: "Save as model" })}
    >
      <TextField
        theme={theme}
        value={name}
        onChangeText={setName}
        autoFocus
        accessibilityLabel={t("library.modelName", { defaultValue: "Model name" })}
        maxLength={80}
        editable={!busy}
      />
      <Text style={styles.muted}>
        {t("library.modelHint", { defaultValue: "Loads become a % of your MAX." })}
      </Text>
      {blocked ? (
        <Text style={styles.muted} accessibilityLiveRegion="polite" aria-live="polite">
          {blocked}
        </Text>
      ) : null}
      {failure ? (
        <Text style={styles.error} accessibilityLiveRegion="polite" aria-live="polite">
          {failure}
        </Text>
      ) : null}
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="ghost"
          label={t("program.cancel", { defaultValue: "Cancel" })}
          onPress={onClose}
          disabled={busy}
        />
        <Button
          theme={theme}
          label={t("program.save", { defaultValue: "Save" })}
          onPress={() => void save()}
          loading={busy}
          disabled={blocked !== null}
          accessibilityHint={blocked ?? undefined}
        />
      </View>
    </Dialog>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    scroll: { gap: s.s3 },
    body: { color: p.ink2, fontSize: ty.sizes.sm },
    muted: { color: p.ink3, fontSize: ty.sizes.sm },
    error: { color: p.danger, fontSize: ty.sizes.sm },
    group: { gap: s.s1 },
    item: { gap: s.s1 },
    bulletRow: { flexDirection: "row", gap: s.s2 },
    flex: { flex: 1 },
    groupTitle: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    actions: { flexDirection: "row", flexWrap: "wrap", justifyContent: "flex-end", gap: s.s3 },
    // Reading order first, look second: Plan first in the source, on the right.
    // Never wider than the dialog: a long label wraps inside its button.
    actionButton: { flexShrink: 1, maxWidth: "100%" },
    actionsReverse: { flexDirection: "row-reverse", justifyContent: "flex-start" },
  });
}
